import { formatShortReference } from '@skjv/common';
import { getBookAbbrev, getLocationTitle, sanitizePdfText } from './pdf-helpers.js';
export async function configureTeacherPage(doc, study, vts, studyGroup, teacher) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginSize = pageWidth * 0.03;
    const padding = 3;
    const bookAbbrev = getBookAbbrev(study.startingLocation);
    const locationTitle = getLocationTitle(study.startingLocation, study.endingLocation);
    const teacherName = teacher?.name || 'Teacher';
    const pageHeader = `${bookAbbrev} ${locationTitle} -- ${study.name}`;
    let currentFontSize = 12;
    let currentY = currentFontSize;
    doc.setFontSize(currentFontSize);
    doc.setTextColor('#333');
    doc.text(pageHeader, pageWidth / 2, currentY, { align: 'center' });
    currentFontSize = 24;
    currentY = currentY + 5;
    doc.rect(marginSize, currentY, pageWidth - 2 * marginSize, currentFontSize * 0.5);
    currentY = currentY + 8;
    currentFontSize = 18;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text(`${teacherName} -- Reading ${bookAbbrev} ${locationTitle}`, pageWidth / 2, currentY, {
        align: 'center',
    });
    currentY = currentY + 8;
    const bottomMargin = 32;
    doc.rect(marginSize, currentY, pageWidth - 2 * marginSize, pageHeight - currentY - bottomMargin);
    currentFontSize = 10;
    currentY = currentY + 5;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('Set these scenes for your students.', marginSize + padding, currentY, { align: 'left' });
    let lines = 1;
    let txt;
    for (const scene of study.scenes) {
        const [statement, ...discussionPoints] = scene.descriptions;
        if (!statement)
            continue;
        currentFontSize = 12;
        doc.setFontSize(currentFontSize);
        doc.setFont('helvetica', 'bold');
        currentY = currentY + 7 * lines;
        txt = doc.splitTextToSize(statement, pageWidth * 0.9);
        doc.text(txt, marginSize + padding, currentY, { align: 'left' });
        lines = txt.length;
        if (discussionPoints.length > 0) {
            const pointsText = discussionPoints
                .filter((p) => p.trim())
                .map((p, i) => `${i + 1}) ${p}`)
                .join('  ');
            if (pointsText) {
                currentFontSize = 10;
                doc.setFontSize(currentFontSize);
                doc.setFont('helvetica', 'normal');
                currentY = currentY + 5 * lines;
                txt = doc.splitTextToSize(pointsText, pageWidth * 0.9 - padding);
                doc.text(txt, marginSize + padding * 2, currentY, { align: 'left' });
                lines = txt.length;
            }
        }
    }
    // Experimental: the reader's "Any thoughts?" seed notes travel onto every
    // teacher's page so the leader teaches from the observations that shaped
    // the AI commentary.
    const readerThoughts = study.readerThoughts?.trim();
    if (readerThoughts) {
        currentFontSize = 10;
        currentY = currentY + 7 * lines;
        doc.setFontSize(currentFontSize);
        doc.setFont('helvetica', 'bold');
        doc.text('Reader thoughts (seeded the AI commentary).', marginSize + padding, currentY, {
            align: 'left',
        });
        currentFontSize = 12;
        currentY = currentY + 5;
        doc.setFontSize(currentFontSize);
        doc.setFont('helvetica', 'italic');
        txt = doc.splitTextToSize(sanitizePdfText(readerThoughts), pageWidth - 2 * marginSize - 2 * padding);
        doc.text(txt, marginSize + padding, currentY, { align: 'left' });
        doc.setFont('helvetica', 'normal');
        currentY = currentY + 5 * (txt.length - 1);
        lines = 1;
    }
    currentFontSize = 10;
    currentY = currentY + 15;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('Students should each do their reading.', marginSize + padding, currentY, { align: 'left' });
    currentFontSize = 10;
    currentY = currentY + 15;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text("Make sure the student's writing looks good.", marginSize + padding, currentY, { align: 'left' });
    currentFontSize = 14;
    currentY = currentY + 5;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'normal');
    const writingVerseText = await vts.getVerseText(study.writingLocation);
    const writingVerseTitle = formatShortReference(study.writingLocation);
    txt = doc.splitTextToSize(`${writingVerseText} -- ${writingVerseTitle}`, pageWidth - 2 * marginSize - 2 * padding);
    doc.text(txt, marginSize + padding, currentY, { align: 'left' });
    lines = txt.length;
    currentY = currentY + 6 * lines + 2;
    currentFontSize = 10;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('Review Questions', marginSize + padding, currentY, { align: 'left' });
    currentFontSize = 14;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'normal');
    lines = 1;
    for (const assignment of study.assignments) {
        currentY = currentY + 6 * lines;
        const questionText = `${assignment.student.name}: ${assignment.reviewQuestion || ''}`;
        txt = doc.splitTextToSize(questionText, pageWidth - 2 * marginSize - 2 * padding);
        lines = txt.length;
        doc.text(txt, marginSize + padding, currentY, { align: 'left' });
    }
    for (const ta of study.teacherAssignments || []) {
        currentY = currentY + 6 * lines;
        const questionText = `${ta.teacher.name}: ${ta.reviewQuestion || ''}`;
        txt = doc.splitTextToSize(questionText, pageWidth - 2 * marginSize - 2 * padding);
        lines = txt.length;
        doc.text(txt, marginSize + padding, currentY, { align: 'left' });
    }
    const memoryStart = study.memoryStartingLocation || studyGroup?.memoryStartingLocation;
    const memoryEnd = study.memoryEndingLocation || studyGroup?.memoryEndingLocation;
    if (memoryStart) {
        currentFontSize = 10;
        currentY = currentY + 14;
        doc.setFontSize(currentFontSize);
        doc.setFont('helvetica', 'bold');
        doc.text(`${teacherName} -- Memory Verse`, marginSize + padding, currentY, { align: 'left' });
        currentFontSize = 14;
        currentY = currentY + 5;
        doc.setFontSize(currentFontSize);
        doc.setFont('helvetica', 'normal');
        const memoryVerseText = await vts.getVerseText(memoryStart, memoryEnd);
        const memoryVerseTitle = vts.getVerseRangeTitle(memoryStart, memoryEnd);
        txt = doc.splitTextToSize(`${memoryVerseText} -- ${memoryVerseTitle}`, pageWidth - 2 * marginSize - 2 * padding);
        doc.text(txt, marginSize + padding, currentY, { align: 'left' });
        lines = txt.length;
        currentY = currentY + 6 * lines + 2;
    }
    currentFontSize = 10;
    currentY = currentY + 12;
    doc.setFontSize(currentFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes and sketches that you may have', marginSize + padding, currentY, { align: 'left' });
    doc.setFont('helvetica', 'normal');
}
//# sourceMappingURL=pdf-teacher-page.js.map